// 반복영역 「패턴 행 설정」 — 캔버스 2단계 픽(시작 행 → 끝 행)의 순수 로직.
//
// 배경(2026-08-28 실측): 항목당 2행+ 반복을 저작할 때 시작/끝 행을 **숫자로** 입력하는 UI가
// 혼선을 낳았다 — 표 아래 채움 행(빈 괘선으로 페이지를 채우는 가상 행)을 실제 행으로 오인해
// 존재하지 않는 행 번호를 넣었다. 처방은 "숫자 입력 대신 캔버스에서 직접 두 행을 찍게 하고,
// 끝 행이 표를 벗어나 채움 행에 떨어지면 그만큼 실제 행을 자동으로 만들어 준다"이다.
//
// DOM 의존(어느 tr이 몇 번째 채움 행인가)은 EditorPage의 클릭 해소가 담당하고, 이 파일은
// **행 번호 산술 · 단계 전이 · rc op 합성**을 갖는다(전부 순수 = 테스트 가능 단위).
// 합성을 여기 두는 이유: 순서가 rc op들의 좌표 시프트·가드와 맞물려 있어 EditorPage에 흩어 두면
// 근거가 사라진다(아래 composePatternRowSet 주석이 그 근거의 정본).
//
// 좌표계 주의: 여기서 다루는 행 번호는 전부 **템플릿 행 인덱스(0-기준)**다. 캔버스 DOM의
// `data-composer-row`는 반복 전개 *후* 물리 인덱스라 서로 다르며, 셀 좌표
// (`data-composer-cell`)는 전개 복제여도 원본 패턴 좌표를 방출하므로 그대로 쓸 수 있다.
//
// 통합(2026-08-28): 종전의 별도 버튼 「행 지정」(밴드 크기 유지 이동 + 매핑 바인딩 동반 이동)을
// 이 픽이 흡수했다. 버튼은 「패턴 행 설정」 하나이고, 옛 밴드와 새 밴드가 **한 행도 겹치지 않으면**
// (disjoint) 매핑을 상대 위치 그대로 새 밴드로 옮긴다. 겹치면 종전처럼 바인딩은 제자리·영역만 갱신.
//   근거: 겹치는 편집은 "범위를 늘리고 줄이는" 조작이라 바인딩이 그 자리에 있는 게 옳고,
//   겹치지 않는 편집은 "패턴을 저기로 옮긴다"라 바인딩이 따라가는 게 옳다.

import {
  addRow,
  planRepeatRegionRowReassign,
  reassignRepeatRegionRow,
  updateRepeatRegion,
  type ReportTemplate,
} from '@safetyhub/report-composer';
import type { TableNode } from '@safetyhub/shared';
import { findNodeById } from './findNodeById';

/** 「패턴 행 설정」 픽의 진행 단계. */
export type PatternPickStage =
  /** 1단계 — 패턴 시작 행을 고르는 중. */
  | { phase: 'start' }
  /** 2단계 — 시작 행이 정해졌고 끝 행을 고르는 중. */
  | { phase: 'end'; rowStart: number };

/** 캔버스 클릭이 해소된 결과(무엇을 눌렀나). */
export type PatternPickInput =
  /** 실제 행의 셀을 눌렀다 — row는 템플릿 행 인덱스. */
  | { kind: 'cell'; row: number }
  /** 표 아래 채움 행을 눌렀다 — virtualRow는 그 자리에 실제 행을 만들면 갖게 될 행 인덱스. */
  | { kind: 'fill'; virtualRow: number };

/** 클릭 1회의 처리 결과. */
export type PatternPickOutcome =
  /** 이 클릭은 못 받는다 — 픽은 유지하고 message로 안내한다. */
  | { type: 'reject'; message: string }
  /** 다음 단계로 진행(픽 유지, 안내 문구 교체). */
  | { type: 'advance'; stage: PatternPickStage }
  /**
   * 범위 확정. rowStart <= targetRow가 보장된다.
   * swapped=true면 사용자가 끝 행을 시작 행보다 위에 찍어 두 값을 맞바꿨다는 뜻(안내 대상).
   */
  | { type: 'commit'; rowStart: number; targetRow: number; swapped: boolean };

/**
 * 채움 tr 순번(1-based) → 가상 행 번호.
 *
 * 표의 마지막 템플릿 행이 `rowCount - 1`이므로 첫 채움 행은 "지금 행을 하나 덧붙이면 갖게 될
 * 번호"인 `rowCount`가 된다. 채움 행은 표 본문 뒤에 연속으로 붙으므로 순번이 곧 덧붙일 행 수다.
 */
export function virtualRowFromFillOrdinal(rowCount: number, fillOrdinal: number): number {
  return rowCount - 1 + fillOrdinal;
}

/**
 * 2단계 픽의 상태 전이(순수).
 *
 * 정책 두 가지:
 *  - **시작 행은 채움 행일 수 없다.** 시작이 표 밖이면 "표 끝에 행을 덧붙인다"가 아니라 표 자체를
 *    늘려야 하는 별개 작업이 되고, 그 사이 행들의 성격(패턴/정적)도 정할 수 없다. 거부 + 안내.
 *  - **끝이 시작보다 위면 맞바꾼다(거부 아님).** 두 점을 찍는 조작에서 순서를 강제하면 사용자는
 *    "왜 안 되지"를 겪고 다시 처음부터 찍어야 한다. 범위의 의미는 순서와 무관하므로 스왑이 안전하다.
 */
export function reducePatternPick(
  stage: PatternPickStage,
  input: PatternPickInput,
): PatternPickOutcome {
  if (stage.phase === 'start') {
    if (input.kind === 'fill') {
      return {
        type: 'reject',
        message: '패턴 시작 행은 표에 있는 실제 행이어야 합니다 — 표 안의 셀을 클릭하세요.',
      };
    }
    return { type: 'advance', stage: { phase: 'end', rowStart: input.row } };
  }
  const picked = input.kind === 'cell' ? input.row : input.virtualRow;
  const swapped = picked < stage.rowStart;
  return {
    type: 'commit',
    rowStart: Math.min(stage.rowStart, picked),
    targetRow: Math.max(stage.rowStart, picked),
    swapped,
  };
}

/** 행 범위(밴드) — 옛/새 반복영역 좌표를 같은 모양으로 다룬다. */
export interface RowBand {
  rowStart: number;
  rowEnd: number;
}

/**
 * 확정 범위를 적용할 때 **기존 매핑 바인딩을 어떻게 다룰지**.
 *  - keep: 제자리(옛 밴드와 새 밴드가 겹침 — 범위를 늘리거나 줄이는 편집).
 *  - relocate: 옛 밴드의 매핑을 새 밴드의 같은 상대 행으로 옮긴다(겹치지 않는 편집 = 이동).
 *    `clampOldRowEnd`는 옮기기 **전에** 옛 밴드를 줄여 둘 높이(= min(옛,새) 높이의 마지막 행)다.
 *    새 밴드가 더 낮으면 옛 밴드 아래쪽 행의 매핑은 옮길 자리가 없어 그대로 남는다(경고 대상).
 */
export type PatternRowSetMove = { kind: 'keep' } | { kind: 'relocate'; clampOldRowEnd: number };

/** 확정된 범위를 템플릿에 적용하기 위한 계획. */
export interface PatternRowSetPlan {
  rowStart: number;
  rowEnd: number;
  /** 표 끝에 새로 만들어야 하는 실제 행 수(0이면 기존 행 범위만 갱신). */
  insertCount: number;
  /** 기존 매핑 처리 방식. oldBand를 주지 않으면 항상 keep. */
  move: PatternRowSetMove;
}

/**
 * 확정 범위 → 적용 계획(순수). 부족분은 **표 끝에 덧붙이는** 행 수다 —
 * 뒤 삽입이라 기존 반복영역 좌표를 건드리지 않고, 그 다음 rowEnd 갱신이 새 행을 밴드에 편입시킨다.
 *
 * `oldBand`(현재 반복영역 좌표)를 주면 새 밴드와의 겹침을 판정해 move를 정한다.
 *
 * null = 적용 불가(빈 표 / 시작 행이 표 밖 / 범위 역전) — 호출부는 no-op + 안내.
 */
export function planPatternRowSet(params: {
  rowCount: number;
  rowStart: number;
  targetRow: number;
  oldBand?: RowBand;
}): PatternRowSetPlan | null {
  const { rowCount, rowStart, targetRow, oldBand } = params;
  if (rowCount <= 0) return null;
  if (rowStart < 0 || rowStart > rowCount - 1) return null;
  if (targetRow < rowStart) return null;
  return {
    rowStart,
    rowEnd: targetRow,
    insertCount: Math.max(0, targetRow - (rowCount - 1)),
    move: planMove(oldBand, { rowStart, rowEnd: targetRow }),
  };
}

/**
 * 겹침 판정 → move. 한 행도 겹치지 않으면 relocate.
 *
 * clampOldRowEnd = 옛 시작 행 + min(옛 높이, 새 높이) - 1.
 * 옮기기 전에 옛 밴드를 이 높이로 줄여야 `reassignRepeatRegionRow`(밴드 크기 유지 이동)의
 * "새 밴드가 표 범위를 벗어나면 no-op" 가드에 걸리지 않는다 — 아래 composePatternRowSet 주석 참조.
 */
function planMove(oldBand: RowBand | undefined, newBand: RowBand): PatternRowSetMove {
  if (!oldBand) return { kind: 'keep' };
  const overlaps = newBand.rowStart <= oldBand.rowEnd && newBand.rowEnd >= oldBand.rowStart;
  if (overlaps) return { kind: 'keep' };
  const oldHeight = oldBand.rowEnd - oldBand.rowStart + 1;
  const newHeight = newBand.rowEnd - newBand.rowStart + 1;
  return { kind: 'relocate', clampOldRowEnd: oldBand.rowStart + Math.min(oldHeight, newHeight) - 1 };
}

/**
 * 옛 밴드 중 **매핑이 살아남는 구간**. 이 밖의 옛 매핑은 정적 행에 남아
 * "매핑했는데 값이 안 나온다"가 되므로 호출부가 개수를 안내한다.
 *  - keep: 새 밴드가 그대로 살아남는 구간(= 좁아진 만큼 밀려남).
 *  - relocate: 옛 밴드의 앞 min(옛,새) 행만 옮겨진다.
 */
export function retainedBandFor(plan: PatternRowSetPlan, oldBand: RowBand): RowBand {
  return plan.move.kind === 'relocate'
    ? { rowStart: oldBand.rowStart, rowEnd: plan.move.clampOldRowEnd }
    : { rowStart: plan.rowStart, rowEnd: plan.rowEnd };
}

/**
 * 새 밴드가 기존 매핑 셀의 행을 벗어나 **정적 행으로 남게 되는** 바인딩 수.
 *
 * 조용히 두면 "매핑했는데 값이 안 나온다"로 나중에 발현하므로 호출부가 토스트로 알린다.
 * 두 번째 밴드로는 `retainedBandFor(plan, oldBand)`(= 살아남는 구간)를 넘긴다.
 */
export function countMappingsDroppedFromBand(
  cells: { row: number; binding?: unknown }[],
  oldBand: RowBand,
  retainedBand: RowBand,
): number {
  return cells.filter(
    (c) =>
      c.binding !== undefined &&
      c.row >= oldBand.rowStart &&
      c.row <= oldBand.rowEnd &&
      !(c.row >= retainedBand.rowStart && c.row <= retainedBand.rowEnd),
  ).length;
}

// ── 템플릿 적용(합성) ─────────────────────────────────────────────────────────
// rc op 합성 순서는 좌표 시프트 규칙과 맞물려 있어 여기 한 곳에 모아 둔다(EditorPage에 흩지 않는다).

/**
 * 합성 1~2단계: 부족분 행 생성 → (relocate면) 옛 밴드 높이 clamp.
 *
 * **행 생성은 항상 표 끝에 덧붙인다.** `shiftRepeatRegionsOnInsert`의 규칙은 `i >= at ? i+1 : i`이고
 * 삽입 지점 at은 매 회 그 시점의 rowCount(=모든 기존 영역 좌표보다 큼)이므로, 기존 반복영역 좌표는
 * 한 칸도 밀리지 않는다. 즉 아래 reassign/갱신이 쓰는 행 번호는 삽입 전후로 동일하다.
 *   (`firstNewRow = rowEnd - insertCount + 1`은 삽입 전 rowCount와 같다: insertCount가
 *    `targetRow - (rowCount - 1)`이므로 rowEnd - insertCount + 1 = rowCount.)
 *
 * clamp가 필요한 이유: `reassignRepeatRegionRow`는 **밴드 크기를 유지**하며 옮기고
 * `newRowStart + 옛높이 - 1 >= rowCount`면 **조용히 no-op**한다. 새 밴드가 옛 밴드보다 낮은데
 * 아래쪽으로 옮기는 경우가 정확히 그 함정이다(예: 옛 [0,5] → 새 [8,8], rowCount 10 → 8+5=13 → no-op).
 * 옮기기 전에 옛 밴드를 min(옛,새) 높이로 줄여 두면 `newRowStart + minH - 1 <= rowEnd <= rowCount-1`이
 * 항상 성립해 가드에 걸리지 않는다. 줄여서 밴드 밖으로 빠진 옛 매핑은 그 자리에 남고(경고 대상),
 * 이는 종전 「패턴 행 설정」이 밴드를 좁혔을 때의 동작과 같다.
 */
function stagePatternRowSet(
  t: ReportTemplate,
  tableId: string,
  regionIndex: number,
  plan: PatternRowSetPlan,
): ReportTemplate {
  let next = t;
  const firstNewRow = plan.rowEnd - plan.insertCount + 1;
  for (let k = 0; k < plan.insertCount; k += 1) {
    next = addRow(next, tableId, firstNewRow + k);
  }
  if (plan.move.kind === 'relocate') {
    next = updateRepeatRegion(next, tableId, regionIndex, { rowEnd: plan.move.clampOldRowEnd });
  }
  return next;
}

/**
 * 확정 계획을 템플릿에 적용한다(순수). 호출부는 이 결과를 **한 번의 apply**로 넣어 단일 undo로 만든다.
 *
 * 순서: 부족분 행 생성 → (relocate면) 옛 밴드 clamp → `reassignRepeatRegionRow`(매핑 동반 이동)
 *       → `updateRepeatRegion`으로 최종 밴드 확정(높이 변화 흡수).
 */
export function composePatternRowSet(
  t: ReportTemplate,
  tableId: string,
  regionIndex: number,
  plan: PatternRowSetPlan,
): ReportTemplate {
  let next = stagePatternRowSet(t, tableId, regionIndex, plan);
  if (plan.move.kind === 'relocate') {
    next = reassignRepeatRegionRow(next, tableId, regionIndex, plan.rowStart);
  }
  return updateRepeatRegion(next, tableId, regionIndex, {
    rowStart: plan.rowStart,
    rowEnd: plan.rowEnd,
  });
}

/**
 * relocate 시 **옮기지 못한 칼럼 수**(대상 셀이 이미 내용/매핑을 가짐 → 옛 매핑만 제거된다).
 * 새로 만든 행은 빈 셀이라 점유가 아니므로, 판정은 반드시 **행 생성 이후 상태**에서 해야 한다
 * (그래서 `stagePatternRowSet`을 거친 뒤 rc의 계획 함수에 묻는다 — 점유 규칙을 여기서 흉내 내지 않는다).
 */
export function countRelocateSkips(
  t: ReportTemplate,
  tableId: string,
  regionIndex: number,
  plan: PatternRowSetPlan,
): number {
  if (plan.move.kind !== 'relocate') return 0;
  const staged = stagePatternRowSet(t, tableId, regionIndex, plan);
  const node = findNodeById(staged.body, tableId);
  if (!node || node.kind !== 'table') return 0;
  const table: TableNode = node;
  return planRepeatRegionRowReassign(table, regionIndex, plan.rowStart)?.skips.length ?? 0;
}
